import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { DocumentsAPI } from 'api/document/document-core';
import { ProjectId, SpaceId, SpaceTabId } from 'api/models';
import { Space, SpaceAPI, SpaceTabAPI } from 'api/space';
import {
  CreateSpaceEmbedPayload,
  CreateSpaceFromTemplatepayload,
  CreateSpaceTabPayload,
  GetSpaceIdResponse,
  SpaceTab,
  UpdateSpaceTabPayload,
} from 'api/space/space-tab';
import { getProjectSpacesQueryKey } from 'queries';
import { fiveMinutes, tenMinutes } from './constants';

const defaultOptions = {
  retry: 1,
  refetchOnWindowFocus: false,
  staleTime: fiveMinutes,
};

type SpaceTabQueryKey = [string, number];

type CreateSpacePayload =
  | CreateSpaceEmbedPayload
  | CreateSpaceTabPayload
  | CreateSpaceFromTemplatepayload;

const getSpaceTabQueryKey = (spaceTabId: number): SpaceTabQueryKey => [
  'space-tab',
  spaceTabId,
];

export const useSpaceTabQuery = (
  spaceTabId: number,
  options: UseQueryOptions<SpaceTab, unknown, SpaceTab, SpaceTabQueryKey> = {},
) => {
  return useQuery(
    getSpaceTabQueryKey(spaceTabId),
    () => SpaceTabAPI.getSpaceTab(spaceTabId),
    { staleTime: tenMinutes, ...options },
  );
};

export const useSpaceIdQuery = (
  spaceTabId: number,
  options: UseQueryOptions<
    GetSpaceIdResponse,
    unknown,
    GetSpaceIdResponse,
    SpaceTabQueryKey
  > = {},
) => {
  return useQuery(
    getSpaceTabQueryKey(spaceTabId),
    () => SpaceTabAPI.getSpaceId(spaceTabId),
    { staleTime: tenMinutes, ...options },
  );
};

export const useSpaceTabMutation = (
  spaceTabId: number,
): UseMutationResult<SpaceTab, unknown, UpdateSpaceTabPayload> => {
  const queryClient = useQueryClient();

  return useMutation<SpaceTab, unknown, UpdateSpaceTabPayload>(
    (payload) => SpaceTabAPI.updateSpaceTab(spaceTabId, payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getSpaceTabQueryKey(spaceTabId));
      },
    },
  );
};

export const useBulkReorderSpaceTabs = (projectId) => {
  const queryClient = useQueryClient();

  return useMutation((payload: any) => SpaceAPI.bulkReorderSpaceTabs(payload), {
    onSuccess: async () => {
      await queryClient.invalidateQueries(
        getProjectSpacesQueryKey(ProjectId(Number(projectId))),
      );
    },
  });
};

export const useBulkReorderSpaces = () => {
  return useMutation((payload: any) => SpaceAPI.bulkReorderSpaces(payload), {
    onSuccess: async () => {},
  });
};

export const useDeleteSpaceMutation = (
  [, , spaceId]: SpaceId,
  options: UseMutationOptions,
) => {
  return useMutation(() => SpaceAPI.deleteSpace(spaceId), options);
};

export const useSpaceCreateMutation = (): UseMutationResult<
  Space,
  unknown,
  Space,
  unknown
> => {
  return useMutation<Space, unknown, Space, unknown>((space: Space) =>
    SpaceAPI.createSpace(space),
  );
};

export const useSpaceUpdateMutation = (
  options: UseMutationOptions<Space, unknown, Space, unknown> = {},
): UseMutationResult<Space, unknown, Space, unknown> => {
  return useMutation<Space, unknown, Space, unknown>(
    (space: Space) => SpaceAPI.updateSpace(space.spaceId, space),
    { ...defaultOptions, ...options },
  );
};

export const useSpaceDeleteMutation = (
  options: UseMutationOptions<unknown, unknown, number, unknown> = {},
): UseMutationResult<unknown, unknown, number, unknown> => {
  return useMutation<unknown, unknown, number, unknown>(
    (spaceId: number) => SpaceAPI.deleteSpace(spaceId),
    { ...defaultOptions, ...options },
  );
};

export const useSpaceTabCreateMutation = (
  options: UseMutationOptions<
    SpaceTab,
    unknown,
    CreateSpacePayload,
    unknown
  > = {},
): UseMutationResult<SpaceTab, unknown, CreateSpacePayload, unknown> => {
  return useMutation<SpaceTab, unknown, CreateSpacePayload, unknown>(
    (spaceTab: CreateSpacePayload) => SpaceTabAPI.createSpaceTab(spaceTab),
    { ...defaultOptions, ...options },
  );
};

export const useSpaceTabDeleteMutation = (
  options: UseMutationOptions<SpaceTab, unknown, number, unknown> = {},
): UseMutationResult<SpaceTab, unknown, number, unknown> =>
  useMutation<SpaceTab, unknown, number, unknown>(
    (spaceTabId: number) => SpaceTabAPI.deleteSpaceTab(spaceTabId),
    { ...defaultOptions, ...options },
  );

export const useSpaceTabTitleMutation = (
  updatedSpaceTabId: SpaceTabId<number>,
) => {
  const queryClient = useQueryClient();
  const [, projectId, spaceId, spaceTabId] = updatedSpaceTabId;

  return useMutation(
    (title: string) =>
      DocumentsAPI.updateDocumentTitle(
        updatedSpaceTabId[1],
        updatedSpaceTabId[3],
        title,
      ),
    {
      onMutate: async (title: string) => {
        const spacesListByProject: Space[] =
          queryClient.getQueryData(
            getProjectSpacesQueryKey(ProjectId(projectId)),
          ) ?? [];

        const updatedSpaces = spacesListByProject.map((space) => {
          if (space.spaceId === Number(spaceId)) {
            return {
              ...space,
              spaceTabs: space.spaceTabs.map((spaceTab) => {
                if (spaceTab.spaceTabId === spaceTabId) {
                  return { ...spaceTab, spaceTabTitle: title };
                }

                return spaceTab;
              }),
            };
          }

          return space;
        });

        await queryClient.setQueryData(
          getProjectSpacesQueryKey(ProjectId(projectId)),
          updatedSpaces,
        );
      },

      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectSpacesQueryKey(ProjectId(projectId)),
        );
      },
    },
  );
};
